import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ToyControlSummary, ToysConfigResponse } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type ControlDraft = {
  label: string;
  amountCoins: string;
};

const emptyDraft = (): ControlDraft => ({
  label: '',
  amountCoins: '25',
});

export function ModelToysCard(): ReactElement {
  const { api } = useAuth();
  const [config, setConfig] = useState<ToysConfigResponse | null>(null);
  const [draft, setDraft] = useState<ControlDraft>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.getMyToys();
    setConfig(result);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.toys.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function saveEnabled(enabled: boolean): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      const result = await api.updateMyToys({ enabled });
      setConfig(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.toys.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAdd(): Promise<void> {
    const amount = Number.parseInt(draft.amountCoins, 10);
    if (!draft.label.trim() || !Number.isFinite(amount) || amount < 1) {
      setError(t('common.studio.toys.invalidControl'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await api.createToyControl({ label: draft.label.trim(), amountCoins: amount });
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.toys.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleEnabled(control: ToyControlSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateToyControl(control.id, { enabled: !control.enabled });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.toys.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(controlId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteToyControl(controlId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.toys.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.toys.loading')}</p>;
  }

  if (!config) {
    return <p className="fm-error">{t('common.studio.toys.loadError')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.toys.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.toys.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          type="checkbox"
          checked={config.settings.enabled}
          disabled={isSaving}
          onChange={(event) => {
            void saveEnabled(event.target.checked);
          }}
        />
        {t('common.studio.toys.enabled')}
      </label>

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
        {config.controls.map((control) => (
          <li
            key={control.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--fm-color-border)',
              opacity: control.enabled ? 1 : 0.55,
            }}
          >
            <span style={{ flex: '1 1 10rem', fontWeight: 600 }}>
              {control.label} · {control.amountCoins}
            </span>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={control.enabled}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleEnabled(control);
                }}
              />
              {t('common.studio.toys.enabled')}
            </label>
            <Button
              label={t('common.studio.toys.delete')}
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                void handleDelete(control.id);
              }}
            />
          </li>
        ))}
      </ul>

      {config.controls.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.toys.empty')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
        <label>
          {t('common.studio.toys.label')}
          <input
            className="fm-input"
            value={draft.label}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, label: event.target.value });
            }}
            placeholder={t('common.studio.toys.labelPlaceholder')}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.toys.amount')}
          <input
            className="fm-input"
            type="number"
            min={1}
            value={draft.amountCoins}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, amountCoins: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <Button
          label={isSaving ? t('common.studio.toys.saving') : t('common.studio.toys.add')}
          disabled={isSaving}
          onClick={() => {
            void handleAdd();
          }}
        />
      </div>
    </div>
  );
}
